import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AppConfig } from '@/types';
import {
  loadConfig,
  saveConfig,
  validateConfig,
  getDefaultConfig,
} from '@/services/storage';

interface AppConfigContextValue {
  config: AppConfig;
  isConfigured: boolean;
  updateConfig: (updates: Partial<AppConfig>) => void;
  resetConfig: () => void;
  validateAndSave: (newConfig: AppConfig) => { valid: boolean; errors: string[] };
}

const AppConfigContext = createContext<AppConfigContextValue | undefined>(undefined);

export function AppConfigProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<AppConfig>(() => {
    const stored = loadConfig();
    return stored || getDefaultConfig();
  });

  const [isConfigured, setIsConfigured] = useState(() => {
    const stored = loadConfig();
    return stored !== null;
  });

  // Update config in state and localStorage
  const updateConfig = useCallback((updates: Partial<AppConfig>) => {
    setConfig((prev) => {
      const updated = { ...prev, ...updates };
      saveConfig(updated);
      return updated;
    });
  }, []);

  // Reset to default config
  const resetConfig = useCallback(() => {
    const defaultConfig = getDefaultConfig();
    setConfig(defaultConfig);
    setIsConfigured(false);
    // Note: Don't clear localStorage here, just reset state
  }, []);

  // Validate and save new config
  const validateAndSave = useCallback((newConfig: AppConfig) => {
    const validation = validateConfig(newConfig);

    if (validation.valid) {
      saveConfig(newConfig);
      setConfig(newConfig);
      setIsConfigured(true);
    }

    return validation;
  }, []);

  // Sync with localStorage changes (from other tabs)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'agent-console-config' && e.newValue) {
        try {
          const newConfig = JSON.parse(e.newValue) as AppConfig;
          setConfig(newConfig);
          setIsConfigured(true);
        } catch (error) {
          console.error('Failed to parse config from storage event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const value: AppConfigContextValue = {
    config,
    isConfigured,
    updateConfig,
    resetConfig,
    validateAndSave,
  };

  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  );
}

export function useAppConfig() {
  const context = useContext(AppConfigContext);
  if (context === undefined) {
    throw new Error('useAppConfig must be used within AppConfigProvider');
  }
  return context;
}
